import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Globe, Menu, Phone } from "lucide-react";
import { Logo } from "./Logo";
import { NAV, NAV_2, PHONE_TEL, PHONE_DISPLAY } from "@/lib/site";
import { useCurrency, CURRENCIES } from "@/context/CurrencyContext";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";

const linkCls = ({ isActive }) =>
  `relative py-1 text-[13px] font-semibold transition-colors ${isActive ? "text-brand" : "text-ink hover:text-brand"}`;

const CurrencySelect = ({ testId }) => {
  const { currency, setCurrency } = useCurrency();
  return (
    <label className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-xs font-bold text-ink">
      <Globe className="h-4 w-4 text-slate-500" />
      <select value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid={testId} className="cursor-pointer bg-transparent outline-none" aria-label="Currency">
        {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code}</option>)}
      </select>
    </label>
  );
};

export const SiteHeader = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 border-b border-slate-100 bg-white/95 backdrop-blur-md" data-testid="site-header">
      <div className="mx-auto flex min-h-[76px] max-w-[1320px] items-center justify-between gap-3 px-4 sm:px-8">
        <Link to="/" data-testid="header-home-link"><Logo /></Link>
        <nav className="hidden items-center gap-3.5 2xl:flex" aria-label="Main navigation">
          {NAV.map((n) => <NavLink key={n.to} to={n.to} className={linkCls} data-testid={`nav-${n.label.toLowerCase()}`}>{n.label}</NavLink>)}
          <span className="h-5 w-px bg-slate-200" />
          {NAV_2.map((n) => <NavLink key={n.to} to={n.to} className={linkCls} data-testid={`nav-${n.label.toLowerCase()}`}>{n.label}</NavLink>)}
        </nav>
        <div className="flex items-center gap-3">
          <div className="hidden lg:block"><CurrencySelect testId="currency-select" /></div>
          <a href={PHONE_TEL} data-testid="header-call-btn" className="group hidden shrink-0 items-center gap-2.5 rounded-lg bg-brand px-3.5 py-2.5 text-white transition-colors duration-200 hover:bg-ink md:flex">
            <span className="grid h-8 w-8 place-items-center border-r border-white/25 pr-2.5">
              <Phone className="h-4 w-4" />
            </span>
            <span className="leading-tight">
              <span className="block text-[9px] font-semibold text-white/80">
                U.S. TOLL-FREE · CALL TO BOOK
              </span>
              <span className="whitespace-nowrap font-display text-base font-extrabold">{PHONE_DISPLAY}</span>
            </span>
          </a>
          <button onClick={() => setOpen(true)} className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-slate-100 text-ink 2xl:hidden" aria-label="Open menu" data-testid="mobile-menu-btn">
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-[300px] bg-white" data-testid="mobile-menu">
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <div className="mt-8 flex flex-col gap-1">
            {[...NAV, ...NAV_2].map((n) => (
              <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                className={({ isActive }) => `rounded-lg px-3 py-3 font-display text-base font-bold ${isActive ? "bg-brand-soft text-brand" : "text-ink"}`}>{n.label}</NavLink>
            ))}
          </div>
          <div className="mt-6 space-y-3">
            <CurrencySelect testId="mobile-currency-select" />
            <a href={PHONE_TEL} data-testid="mobile-menu-call" className="flex items-center justify-center gap-2 rounded-xl bg-coral py-3.5 font-bold text-white"><Phone className="h-4 w-4" />{PHONE_DISPLAY}</a>
          </div>
        </SheetContent>
      </Sheet>
    </header>
  );
};
